import { useEffect, useState } from "react";
import { api, formatCents, formatDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Card, Button, Pill, Loader, EmptyState, Modal, Input, Select, Stat } from "@/components/ui/Primitives";
import { unwrap } from "@/lib/unwrap";
import { Zap, Plus, RefreshCw, Receipt, Droplet, Flame, Wifi } from "lucide-react";
import { toast } from "sonner";

const ICONS = { electricity: Zap, water: Droplet, gas: Flame, internet: Wifi };

export default function Utilities() {
  const { user } = useAuth();
  const isLandlord = user?.role === "landlord";
  const [tab, setTab] = useState("accounts");
  const [accounts, setAccounts] = useState([]);
  const [bills, setBills] = useState([]);
  const [tenantCharges, setTenantCharges] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openAcc, setOpenAcc] = useState(false);
  const [openBill, setOpenBill] = useState(false);
  const [saving, setSaving] = useState(false);
  const [accForm, setAccForm] = useState({ property_id: "", utility_type: "electricity", provider: "", account_number: "" });
  const [billForm, setBillForm] = useState({ account_id: "", period: "", amount_cents: "", due_date: "" });

  const load = async () => {
    setLoading(true);
    try {
      // Only landlords have permission for these endpoints.
      if (!isLandlord) {
        setAccounts([]); setBills([]); setTenantCharges([]); setProperties([]);
        return;
      }
      const [a, b, c, p] = await Promise.all([
        api.get("/utilities/accounts").catch(() => ({ data: [] })),
        api.get("/utilities/bills").catch(() => ({ data: [] })),
        api.get("/utilities/tenant-charges").catch(() => ({ data: [] })),
        api.get("/properties").catch(() => ({ data: [] })),
      ]);
      setAccounts(unwrap(a.data, "accounts"));
      setBills(unwrap(b.data, "bills"));
      setTenantCharges(unwrap(c.data, "charges"));
      setProperties(unwrap(p.data, "properties"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submitAccount = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/utilities/accounts", accForm);
      toast.success("Account added");
      setOpenAcc(false);
      setAccForm({ property_id: "", utility_type: "electricity", provider: "", account_number: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add");
    } finally { setSaving(false); }
  };

  const submitBill = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/utilities/bills", {
        account_id: billForm.account_id,
        period: billForm.period,
        amount_cents: Math.round(Number(billForm.amount_cents || 0) * 100),
        due_date: billForm.due_date || null,
      });
      toast.success("Bill added");
      setOpenBill(false);
      setBillForm({ account_id: "", period: "", amount_cents: "", due_date: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add bill");
    } finally { setSaving(false); }
  };

  const sync = async () => {
    try {
      await api.post("/utilities/reminders/sync");
      toast.success("Reminders synced");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sync failed");
    }
  };

  if (loading) return <Loader/>;

  const total = bills.reduce((s, b) => s + (b.amount_cents || 0), 0);
  const tenantTotal = tenantCharges.reduce((s, c) => s + (c.amount_cents || 0), 0);

  const tabs = [
    { key: "accounts", label: "Accounts", count: accounts.length },
    { key: "bills", label: "Bills", count: bills.length },
    { key: "charges", label: "Tenant charges", count: tenantCharges.length },
  ];

  return (
    <div>
      <PageHeader
        title="Utilities"
        subtitle="Hydro, water, gas, internet — track utilities and chargebacks across the portfolio."
        actions={isLandlord && (
          <>
            <Button variant="secondary" onClick={sync} data-testid="util-sync-button"><RefreshCw className="w-4 h-4"/> Sync reminders</Button>
            {tab === "accounts" && <Button onClick={() => setOpenAcc(true)} data-testid="add-util-account-button"><Plus className="w-4 h-4"/> Add account</Button>}
            {tab === "bills" && <Button onClick={() => setOpenBill(true)} disabled={accounts.length === 0} data-testid="add-util-bill-button"><Plus className="w-4 h-4"/> Add bill</Button>}
          </>
        )}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat label="Active accounts" value={accounts.length} icon={Zap}/>
        <Stat label="Bills this period" value={bills.length} icon={Receipt}/>
        <Stat label="Total billed" value={formatCents(total)} accent="text-green-800"/>
        <Stat label="Tenant charges" value={formatCents(tenantTotal)} accent="text-amber-600"/>
      </div>

      <div className="flex gap-1 border-b border-slate-200 mb-5 overflow-x-auto">
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`util-tab-${t.key}`}
            className={`px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${tab === t.key ? "border-green-800 text-slate-950" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
            {t.label} <span className="text-xs text-slate-400">({t.count})</span>
          </button>
        ))}
      </div>

      {tab === "accounts" && (
        accounts.length === 0 ? (
          <EmptyState icon={Zap} title="No utility accounts yet" description="Add hydro, water, gas, or internet accounts to start tracking." action={null}/>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {accounts.map((a) => {
              const Icon = ICONS[a.utility_type] || Zap;
              return (
                <Card key={a.account_id} className="p-5 card-lift" data-testid={`util-account-${a.account_id}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center flex-none"><Icon className="w-5 h-5"/></div>
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 capitalize">{a.utility_type}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{a.provider}</div>
                      <div className="text-[11px] text-slate-400 font-mono mt-1">#{a.account_number}</div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )
      )}

      {tab === "bills" && (
        bills.length === 0 ? (
          <EmptyState icon={Receipt} title="No bills tracked" description="Once you log a bill, it will appear here with chargeback options."/>
        ) : (
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <tr><th className="px-5 py-3">Period</th><th className="px-5 py-3">Utility</th><th className="px-5 py-3">Due</th><th className="px-5 py-3 text-right">Amount</th></tr>
              </thead>
              <tbody>
                {bills.map((b) => (
                  <tr key={b.bill_id} className="border-b border-slate-100" data-testid={`util-bill-${b.bill_id}`}>
                    <td className="px-5 py-3 font-semibold text-slate-900">{b.period}</td>
                    <td className="px-5 py-3 capitalize text-slate-600">{b.utility_type || accounts.find((a) => a.account_id === b.account_id)?.utility_type || "—"}</td>
                    <td className="px-5 py-3 text-slate-600">{b.due_date ? formatDate(b.due_date) : "—"}</td>
                    <td className="px-5 py-3 text-right font-semibold">{formatCents(b.amount_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )
      )}

      {tab === "charges" && (
        tenantCharges.length === 0 ? (
          <EmptyState icon={Receipt} title="No tenant chargebacks" description="When a bill is split with tenants, their portion appears here."/>
        ) : (
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <tr><th className="px-5 py-3">Tenant</th><th className="px-5 py-3">Period</th><th className="px-5 py-3">Status</th><th className="px-5 py-3 text-right">Amount</th></tr>
              </thead>
              <tbody>
                {tenantCharges.map((c, i) => (
                  <tr key={c.charge_id || i} className="border-b border-slate-100" data-testid={`util-charge-${i}`}>
                    <td className="px-5 py-3 font-semibold text-slate-900">{c.tenant_email || c.tenant_name || "Tenant"}</td>
                    <td className="px-5 py-3 text-slate-600">{c.period}</td>
                    <td className="px-5 py-3"><Pill tone={c.status === "paid" ? "green" : "amber"}>{c.status || "pending"}</Pill></td>
                    <td className="px-5 py-3 text-right font-semibold">{formatCents(c.amount_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )
      )}

      <Modal open={openAcc} onClose={() => setOpenAcc(false)} title="Add utility account">
        <form onSubmit={submitAccount} className="space-y-4">
          <Select label="Property" required value={accForm.property_id} onChange={(e) => setAccForm({ ...accForm, property_id: e.target.value })}>
            <option value="">Select property</option>
            {properties.map((p) => <option key={p.property_id} value={p.property_id}>{p.name}</option>)}
          </Select>
          <Select label="Utility" value={accForm.utility_type} onChange={(e) => setAccForm({ ...accForm, utility_type: e.target.value })}>
            <option value="electricity">Electricity / Hydro</option>
            <option value="water">Water</option>
            <option value="gas">Natural gas</option>
            <option value="internet">Internet</option>
          </Select>
          <Input label="Provider" required value={accForm.provider} onChange={(e) => setAccForm({ ...accForm, provider: e.target.value })}/>
          <Input label="Account number" required value={accForm.account_number} onChange={(e) => setAccForm({ ...accForm, account_number: e.target.value })}/>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setOpenAcc(false)}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Add"}</Button>
          </div>
        </form>
      </Modal>

      <Modal open={openBill} onClose={() => setOpenBill(false)} title="Add utility bill">
        <form onSubmit={submitBill} className="space-y-4">
          <Select label="Account" required value={billForm.account_id} onChange={(e) => setBillForm({ ...billForm, account_id: e.target.value })}>
            <option value="">Select account</option>
            {accounts.map((a) => <option key={a.account_id} value={a.account_id}>{`${a.utility_type} · ${a.provider}`}</option>)}
          </Select>
          <Input label="Period (e.g. 2026-06)" required value={billForm.period} onChange={(e) => setBillForm({ ...billForm, period: e.target.value })}/>
          <Input label="Amount (CAD)" type="number" required value={billForm.amount_cents} onChange={(e) => setBillForm({ ...billForm, amount_cents: e.target.value })}/>
          <Input label="Due date" type="date" value={billForm.due_date} onChange={(e) => setBillForm({ ...billForm, due_date: e.target.value })}/>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setOpenBill(false)}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Add bill"}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
